export class OrbitCamera extends pc.ScriptType {

    initialize() {
        this.autoRender = true;
        this.distanceMax = 0;
        this.distanceMin = 0;
        this.pitchAngleMax = 90; //up & down
        this.pitchAngleMin = -90; //0 for floor lock
        this.yawAngleMax = 0; //right & left
        this.yawAngleMin = 0; //0 = no clamp
        this.inertiaFactor = 0.5;
        this.focusEntity = null;
        this.frameOnStart = false;

        this._checkAspectRatio();
    
        // Find all the models in the scene that are under the focused entity
        this._modelsAabb = new pc.BoundingBox();
        this._buildAabb(this.focusEntity || this.app.root, 0);
    
        this.entity.lookAt(this._modelsAabb.center);
    
        this._pivotPoint = new pc.Vec3();
        this._pivotPoint.copy(this._modelsAabb.center);
    
        this._lastFramePivotPoint = this._pivotPoint.clone();
    
        // Calculate the camera euler angle rotation around x and y axes
        // This allows us to place the camera at a particular rotation to begin with in the scene
        let cameraQuat = this.entity.getRotation();
        this.distanceBetween = new pc.Vec3();
        this.quatWithoutYaw = new pc.Quat();
        this.yawOffset = new pc.Quat();
    
        // Preset the camera
        this._yaw = this._calcYaw(cameraQuat);
        this._pitch = this._clampPitchAngle(this._calcPitch(cameraQuat, this._yaw));
        this.entity.setLocalEulerAngles(this._pitch, this._yaw, 0);
    
        this._distance = 0;
        this._targetYaw = this._yaw;
        this._targetPitch = this._pitch;
    
        // If we have ticked focus on start, then attempt to position the camera where it frames
        // the focused entity and move the pivot point to entity's position otherwise, set the distance
        // to be between the camera position in the scene and the pivot point
        if (this.frameOnStart) {
            this.focus(this.focusEntity || this.app.root);
        } else {
            let distanceBetween = new pc.Vec3();
            distanceBetween.sub2(this.entity.getPosition(), this._pivotPoint);
            this._distance = this._clampDistance(distanceBetween.length());
        }
    
        this._targetDistance = this._distance;
    
        this._autoRenderDefault = this.app.autoRender;
    
        // Do not enable autoRender if it's already off as it's controlled elsewhere
        if (this.app.autoRender) {
            this.app.autoRender = this.autoRender;
        }
    
        if (!this.autoRender) {
            this.app.renderNextFrame = true;
        }
    
        this.on('attr:autoRender', function (value, prev) {
            this.app.autoRender = value;
            if (!this.autoRender) {
                this.app.renderNextFrame = true;
            }
        }, this);
    
        // Reapply the clamps if they are changed in the editor
        this.on('attr:distanceMin', function (value, prev) {
            this._targetDistance = this._clampDistance(this._distance);
        }, this);
    
        this.on('attr:distanceMax', function (value, prev) {
            this._targetDistance = this._clampDistance(this._distance);
        }, this);
    
        this.on('attr:pitchAngleMin', function (value, prev) {
            this._targetPitch = this._clampPitchAngle(this._pitch);
        }, this);
    
        this.on('attr:pitchAngleMax', function (value, prev) {
            this._targetPitch = this._clampPitchAngle(this._pitch);
        }, this);
    
        // Focus on the entity if we change the focus entity
        this.on('attr:focusEntity', function (value, prev) {
            if (this.frameOnStart) {
                this.focus(value || this.app.root);
            } else {
                this.resetAndLookAtEntity(this.entity.getPosition(), value || this.app.root);
            }
        }, this);
    
        this.on('attr:frameOnStart', function (value, prev) {
            if (value) {
                this.focus(this.focusEntity || this.app.root);
            }
        }, this);
    
        let onResizeCanvas = function () {
            this._checkAspectRatio();
            if (!this.autoRender) {
                this.app.renderNextFrame = true;
            }
        };
    
        this.app.graphicsDevice.on('resizecanvas', onResizeCanvas, this);
    
        this.on('destroy', function() {
            this.app.graphicsDevice.off('resizecanvas', onResizeCanvas, this);
            this.app.autoRender = this._autoRenderDefault;
        }, this);
    }


    get distance() {
        return this._targetDistance;
    }

    set distance(value) {
        this._targetDistance = this._clampDistance(value);
    }

    get pitch() {
        return this._targetPitch;
    }

    set pitch(value) {
        this._targetPitch = this._clampPitchAngle(value);
    }

    get yaw() {
        return this._targetYaw;
    }

    set yaw(value) {
        this._targetYaw = value;

        // Ensure that the yaw takes the shortest route by making sure that 
        // the difference between the targetYaw and the actual is 180 degrees
        // in either direction
        let diff = this._targetYaw - this._yaw;
        let reminder = diff % 360;
        if (reminder > 180) {
            this._targetYaw = this._yaw - (360 - reminder);
        } else if (reminder < -180) {
            this._targetYaw = this._yaw + (360 + reminder);
        } else {
            this._targetYaw = this._yaw + reminder;
        }
    }

    get pivotPoint() {
        return this._pivotPoint;
    }

    set pivotPoint(value) {
        this._pivotPoint.copy(value);
    }

    focus(focusEntity) {
        // Calculate an bounding box that encompasses all the models to frame in the camera view
        this._buildAabb(focusEntity, 0);

        let halfExtents = this._modelsAabb.halfExtents;

        let distance = Math.max(halfExtents.x, Math.max(halfExtents.y, halfExtents.z));
        distance = (distance / Math.tan(0.5 * this.entity.camera.fov * pc.math.DEG_TO_RAD));
        distance = (distance * 2);

        this.distance = distance;

        this._removeInertia();

        this._pivotPoint.copy(this._modelsAabb.center);
    }

    resetAndLookAtPoint(resetPoint, lookAtPoint = new pc.Vec3(0, 0, 0)) {
        this.pivotPoint.copy(lookAtPoint);
        this.entity.setPosition(resetPoint);

        this.entity.lookAt(lookAtPoint);

        let distance = this.distanceBetween;
        distance.sub2(lookAtPoint, resetPoint);
        this.distance = distance.length();

        this.pivotPoint.copy(lookAtPoint);

        let cameraQuat = this.entity.getRotation();
        this.yaw = this._calcYaw(cameraQuat);
        this.pitch = this._calcPitch(cameraQuat, this.yaw);

        this._removeInertia();
        this._updatePosition();

        if (!this.autoRender) {
            this.app.renderNextFrame = true;
        }
    }


    resetAndLookAtEntity = function (resetPoint, entity) {
        this._buildAabb(entity, 0);
        this.resetAndLookAtPoint(resetPoint, this._modelsAabb.center);
    }

    reset = function (yaw, pitch, distance) {
        this.pitch = pitch;
        this.yaw = yaw;
        this.distance = distance;

        this._removeInertia();

        if (!this.autoRender) {
            this.app.renderNextFrame = true;
        }
    }

    setCameraAction = function (distanceMax, distanceMin, pitchAngleMax, pitchAngleMin, yawAngleMax, yawAngleMin) {
        this.distanceMax = distanceMax;
        this.distanceMin = distanceMin;
        this.pitchAngleMax = pitchAngleMax;
        this.pitchAngleMin = pitchAngleMin;
        this.yawAngleMax = yawAngleMax;
        this.yawAngleMin = yawAngleMin;
    };

    update = function(dt) {
        // Check if we have are still moving for autorender
        if (!this.autoRender) {
            let distanceDiff = Math.abs(this._targetDistance - this._distance);
            let yawDiff = Math.abs(this._targetYaw - this._yaw);
            let pitchDiff = Math.abs(this._targetPitch - this._pitch);
            let pivotPointDiff = this._lastFramePivotPoint.distance(this._pivotPoint);
        
            this.app.renderNextFrame = this.app.renderNextFrame || 
                distanceDiff > 0.01 || yawDiff > 0.01 || pitchDiff > 0.01 || pivotPointDiff > 0;
        }

        // Add inertia, if any
        let t = this.inertiaFactor === 0 ? 1 : Math.min(dt / this.inertiaFactor, 1);
        this._distance = pc.math.lerp(this._distance, this._targetDistance, t);
        this._yaw = pc.math.lerp(this._yaw, this._targetYaw, t);
        this._pitch = pc.math.lerp(this._pitch, this._targetPitch, t);

        this._lastFramePivotPoint.copy(this._pivotPoint);

        this._updatePosition();
    }


    _updatePosition = function () {
        // Work out the camera position based on the pivot point, pitch, yaw and distance
        if(this.yawAngleMax != 0 && this._yaw > this.yawAngleMax){this._yaw = this.yawAngleMax;}
        if(this.yawAngleMin != 0 && this._yaw < this.yawAngleMin){this._yaw = this.yawAngleMin;}

        this.entity.setLocalPosition(0,0,0);
        this.entity.setLocalEulerAngles(this._pitch, this._yaw, 0);

        let position = this.entity.getPosition();
        position.copy(this.entity.forward);
        position.mulScalar(-this._distance);
        position.add(this.pivotPoint);
        this.entity.setPosition(position);
    }


    _removeInertia = function () {
        this._yaw = this._targetYaw;
        this._pitch = this._targetPitch;
        this._distance = this._targetDistance;
    }


    _checkAspectRatio = function () {
        let height = this.app.graphicsDevice.height;
        let width = this.app.graphicsDevice.width;

        // Match the axis of FOV to match the aspect ratio of the canvas so
        // the focused entities is always in frame
        this.entity.camera.horizontalFov = height > width;
    }


    _buildAabb = function (entity, modelsAdded) {
        let i = 0, j = 0, meshInstances;
        
        if (entity instanceof pc.Entity) {
            let allMeshInstances = [];
            let renders = entity.findComponents('render');

            for (i = 0; i < renders.length; ++i) {
                meshInstances = renders[i].meshInstances;
                if (meshInstances) {
                    for (j = 0; j < meshInstances.length; j++) {
                        allMeshInstances.push(meshInstances[j]);
                    }
                }
            }  

            let models = entity.findComponents('model');
            for (i = 0; i < models.length; ++i) {
                meshInstances = models[i].meshInstances;
                if (meshInstances) {
                    for (j = 0; j < meshInstances.length; j++) {
                        allMeshInstances.push(meshInstances[j]);
                    }
                }
            }  

            for (i = 0; i < allMeshInstances.length; i++) {
                if (modelsAdded === 0) {
                    this._modelsAabb.copy(allMeshInstances[i].aabb);
                } else {
                    this._modelsAabb.add(allMeshInstances[i].aabb);
                }

                modelsAdded += 1;
            }
        }

        for (i = 0; i < entity.children.length; ++i) {
            modelsAdded += this._buildAabb(entity.children[i], modelsAdded);
        }

        return modelsAdded;
    }


    _calcYaw = function (quat) {
        let transformedForward = new pc.Vec3();
        quat.transformVector(pc.Vec3.FORWARD, transformedForward);

        return Math.atan2(-transformedForward.x, -transformedForward.z) * pc.math.RAD_TO_DEG;
    }


    _clampDistance = function (distance) {
        if (this.distanceMax > 0) {
            return pc.math.clamp(distance, this.distanceMin, this.distanceMax);
        } else {
            return Math.max(distance, this.distanceMin);
        }
    }


    _clampPitchAngle = function (pitch) {
        // Negative due as the pitch is inversed since the camera is orbiting the entity
        return pc.math.clamp(pitch, -this.pitchAngleMax, -this.pitchAngleMin);
    }

    _calcPitch = function(quat, yaw) {
        let quatWithoutYaw = this.quatWithoutYaw;
        let yawOffset = this.yawOffset;

        yawOffset.setFromEulerAngles(0, -yaw, 0);
        quatWithoutYaw.mul2(yawOffset, quat);

        let transformedForward = new pc.Vec3();

        quatWithoutYaw.transformVector(pc.Vec3.FORWARD, transformedForward);

        return Math.atan2(transformedForward.y, -transformedForward.z) * pc.math.RAD_TO_DEG;
    }
}